package org.hs_soft.runmynesto.pages.home.sub_page.dashboard.mvi

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.hs_soft.runmynesto.data.api_datasource.RequestManager
import org.hs_soft.runmynesto.domain.errorMessage
import org.hs_soft.runmynesto.domain.model.general_error_model.GeneralError
import org.hs_soft.runmynesto.domain.model.home.closing_reports.CurrencyTypeEnum

import org.hs_soft.runmynesto.domain.model.home.dashboard.dashboard_config.ListShopItem
import org.hs_soft.runmynesto.domain.model.home.dashboard.get_shop_statics.ShopStatistic
import org.hs_soft.runmynesto.domain.model.home.options.CountriesEnum
import org.hs_soft.runmynesto.domain.model.home.template.tabs.tab_general.calendar.MonthModel
import org.hs_soft.runmynesto.domain.requestHandler
import org.hs_soft.runmynesto.domain.usecase.dashboard.CustomerFrequencyUseCase
import org.hs_soft.runmynesto.domain.usecase.dashboard.DashboardConfigUseCase
import org.hs_soft.runmynesto.domain.usecase.dashboard.ShopStaticsUseCase
import org.hs_soft.runmynesto.domain.usecase.home.GetCompanyProfileIdUseCase
import org.hs_soft.runmynesto.domain.util.ApiUtils
import org.hs_soft.runmynesto.domain.util.Constants
import org.hs_soft.runmynesto.domain.util.getNextDay
import org.hs_soft.runmynesto.domain.util.helper.BaseViewModel
import org.hs_soft.runmynesto.domain.util.helper.LanguageManager
import org.hs_soft.runmynesto.domain.util.withTokenRefresh
import org.hs_soft.runmynesto.pages.home.sub_page.closing_reports.mvi.ClosingReportsEvent

class DashboardViewModel(
    private val requestManager: RequestManager,
    private val shopStaticsUseCase:ShopStaticsUseCase,
    private val dashboardConfigUseCase: DashboardConfigUseCase,
    private val getCompanyProfileIdUseCase: GetCompanyProfileIdUseCase,
    private val customerFrequencyUseCase: CustomerFrequencyUseCase,

): BaseViewModel() {

    private val _state = MutableStateFlow(DashboardState())
    val state: StateFlow<DashboardState> = _state


    override fun clear() {
        super.clear()
        requestManager.cancelAll()
    }

    init {
        initCalendarToToday()
        initCurrencyAndTimeType()
    }

    private fun initCurrencyAndTimeType() {
        val result=getCompanyProfileIdUseCase()
        _state.value=_state.value.copy(
            selectedCompanyProfile = result
        )

        val currencyType= if (state.value.selectedCompanyProfile== CountriesEnum.SWITZERLAND.id)
             CurrencyTypeEnum.CHF.title
        else CurrencyTypeEnum.EUR.title

        val timeType=if (state.value.selectedCompanyProfile== CountriesEnum.SWITZERLAND.id)
            Constants.stDot
        else Constants.hr

        _state.value=_state.value.copy(
            currencyType = currencyType,
            timeType = timeType,
        )

    }

    private fun initCalendarToToday() {
        val today = Clock.System.now().toLocalDateTime(TimeZone.UTC).date

        _state.value = _state.value.copy(
            selectedDate = MonthModel(
                dayNumber = today.dayOfMonth,
                yearNumber = today.year,
                monthNumber = today.monthNumber,
            )
        )
    }



    fun onEvent(event: DashboardEvent) {

        when(event)
        {

            is DashboardEvent.DismissErrorDialog->{
                _state.value=_state.value.copy(
                    errorInfo = null,
                )
            }

            is DashboardEvent.SetCompareSaleChartDialogStatus->{
                _state.value=_state.value.copy(showCompareSaleChartDialog = event.status)
            }
            is DashboardEvent.OnBackGroundClick->{
                onBackGroundClick()
            }

            is DashboardEvent.FilterBasedOnRegion->{
                filterBasedOnRegion(shopItem=event.shopItem)
            }

            is DashboardEvent.GetOtherDaysCustomerClientsFrequency->{
                getClientsFrequencyData(
                    selectedItemId=state.value.selectedDashboardItem?.branchId?.toString()?:"",
                    comparedDate=event.comparedDate,
                )
            }

            is DashboardEvent.GetOtherDaysCustomerSalesFrequency->{
                getSalesFrequencyData(
                    selectedItemId=state.value.selectedDashboardItem?.branchId?.toString()?:"",
                    comparedDate=event.comparedDate,
                )
            }

            is DashboardEvent.GetCustomerFrequency->{
                getCustomerFrequency(
                    selectedItemId=state.value.selectedDashboardItem?.branchId?.toString()?:"",
                    selectedDate=event.selectedDate,
                )
            }

            is DashboardEvent.OnDetailBackClick->{
                _state.value=_state.value.copy(
                    showDashboardDetail = false
                )
            }
            is DashboardEvent.OnDashboardItemClick->{
                onDashboardItemClick(
                    dashboardItem=event.item,
                    itemName=event.itemName,
                )
            }

            is DashboardEvent.SetCompareClientChartDialogStatus->{
                _state.value=_state.value.copy(
                    showCompareClientChartDialog = event.status
                )
            }


            is DashboardEvent.GetDashboardConfigs->{
                getDashBoardConfig()
            }

            is DashboardEvent.GetShopStatics->{
                getShopStatics(date = state.value.selectedDate)
            }



            is DashboardEvent.SetCalendarStatus->{
                _state.value=_state.value.copy(showCalendarDialog = event.status)
            }

            is DashboardEvent.GetShopListItems->{
                chooseDate(
                    selectedDate=event.selectedDate,
                )
            }


        }


    }

    private fun onBackGroundClick() {
        _state.value=_state.value.copy(
            showCompareClientChartDialog=false,
        )
    }


    private fun getClientsFrequencyData(
        selectedItemId: String,
        comparedDate: MonthModel
    ) {
        if (comparedDate != state.value.selectedDate) {
            launch {
                requestHandler(
                    loadingState = { isLoading ->
                        _state.value = _state.value.copy(dataLoadingProgressVisible = isLoading)
                    },
                    callRequest = {
                        _state.value = _state.value.copy(
                            showCalendarDialog = false,
                            clientCompareDate = comparedDate
                        )

                        customerFrequencyUseCase(
                            cashRegisterIds = getCashRegisterIds(
                                filteredId = selectedItemId,
                                cashRegisters = state.value.listShop
                            ),
                            periodStart = comparedDate.toFormattedDateString(),
                            periodEnd = getNextDay(comparedDate)
                        )
                    },
                    onSuccess = { result ->
                        _state.value = _state.value.copy(
                            comparedClientsCustomerFrequencies = result.data?.cashAssist?.getCustomerFrequencies?.
                            map { it?.billCount } ?: listOf(),
                            showCompareClientChartDialog = false
                        )
                    },
                    onError = { result ->
                        showRequestError(result.errors)
                    },
                    onException = { e ->
                        showException(e)
                    }
                )
            }
        }
    }

    private fun getSalesFrequencyData(
        selectedItemId: String,
        comparedDate: MonthModel
    ) {
        if (comparedDate != state.value.selectedDate) {
            launch {
                requestHandler(
                    loadingState = { isLoading ->
                        _state.value = _state.value.copy(dataLoadingProgressVisible = isLoading)
                    },
                    callRequest = {
                        _state.value = _state.value.copy(
                            showCalendarDialog = false,
                            saleCompareDate = comparedDate
                        )

                        customerFrequencyUseCase(
                            cashRegisterIds = getCashRegisterIds(
                                filteredId = selectedItemId,
                                cashRegisters = state.value.listShop
                            ),
                            periodStart = comparedDate.toFormattedDateString(),
                            periodEnd = getNextDay(comparedDate)
                        )
                    },
                    onSuccess = { result ->
                        _state.value = _state.value.copy(
                            comparedSalesCustomerFrequencies = result.data?.cashAssist?.getCustomerFrequencies?.
                            map { it?.total } ?: listOf(),
                            showCompareSaleChartDialog = false
                        )
                    },
                    onError = { result ->
                        showRequestError(result.errors)
                    },
                    onException = { e ->
                        showException(e)
                    }
                )
            }
        }
    }

    private fun getCustomerFrequency(
        selectedItemId: String,
        selectedDate: MonthModel
    ) {
        launch {
            requestHandler(
                loadingState = { isLoading ->
                    _state.value = _state.value.copy(dataLoadingProgressVisible = isLoading)
                },
                callRequest = {
                    _state.value = _state.value.copy(
                        showCalendarDialog = false,
                        selectedDate = selectedDate
                    )

                    customerFrequencyUseCase(
                        cashRegisterIds = getCashRegisterIds(
                            filteredId = selectedItemId,
                            cashRegisters = state.value.listShop
                        ),
                        periodStart = selectedDate.toFormattedDateString(),
                        periodEnd = getNextDay(selectedDate)
                    )
                },
                onSuccess = { result ->
                    if (result.data?.cashAssist?.getCustomerFrequencies?.size != 0) {
                        _state.value = _state.value.copy(
                            customerFrequencies = result.data?.cashAssist?.getCustomerFrequencies,
                            showDashboardDetail = true
                        )
                    }
                },
                onError = { result ->
                    showRequestError(result.errors)
                },
                onException = { e ->
                    showException(e)
                }
            )
        }
    }

    private fun getDashBoardConfig() {
        launch {
            requestHandler(
                callRequest = {
                    dashboardConfigUseCase()
                },
                onSuccess = { result ->
                    val shopItems = result.data?.cashAssist?.listShops?.items?.toMutableList()

                    shopItems?.add(0,
                        ListShopItem(
                            name = LanguageManager.getCurrentStrings().allTheRegions
                        )
                    )

                    _state.value = _state.value.copy(
                        listShop = shopItems
                    )
                },
                onError = { result ->
                    showRequestError(result.errors)
                },
                onException = { e ->
                    showException(e)
                }
            )
        }
    }

    private fun getShopStatics(date: MonthModel) {
        launch {
            requestHandler(
                loadingState = { isLoading ->
                    _state.value = _state.value.copy(dataLoadingProgressVisible = isLoading)
                },
                callRequest = {
                    shopStaticsUseCase(
                        date = date.toFormattedDateString()
                    )
                },
                onSuccess = { result ->
                    _state.value = _state.value.copy(
                        shopStaticsData = result.data?.getShopsStatisticForDate
                    )
                },
                onError = { result ->
                    showRequestError(result.errors)
                },
                onException = { e ->
                    showException(e)
                }
            )
        }
    }


    private fun filterBasedOnRegion(
        shopItem: ListShopItem?,
    ) {
        _state.value=_state.value.copy(
            selectedShop = shopItem
        )

    }





    private fun onDashboardItemClick(
        dashboardItem: ShopStatistic,
        itemName: String
    ) {
        _state.value=_state.value.copy(
            selectedDashboardItem=dashboardItem,
            dashboardItemName=itemName,
            comparedClientsCustomerFrequencies = listOf(),
            clientCompareDate = state.value.selectedDate,
            saleCompareDate = state.value.selectedDate,
        )
        getCustomerFrequency(
            selectedItemId=dashboardItem.branchId.toString(),
            selectedDate = state.value.selectedDate,
        )
    }





    private fun chooseDate(
        selectedDate: MonthModel,
    ) {
        _state.value = _state.value.copy(
            showCalendarDialog = false,
            selectedDate =  selectedDate,
        )

        getShopStatics(date = selectedDate)
    }


    private fun MonthModel.toFormattedDateString(): String {
        return "${yearNumber}-${monthNumber.toString()
            .padStart(2, '0')}-${dayNumber.toString()
                .padStart(2, '0')}"
    }





    private fun getCashRegisterIds(
        filteredId: String?=null,
        cashRegisters: List<ListShopItem?>?
    ): List<String> {
        return cashRegisters
            ?.filter { it != null && (filteredId == null || it.id == filteredId) }
            ?.flatMap { it?.cashRegisters.orEmpty() }
            ?.mapNotNull { it?.id }
            ?: emptyList()
    }




    private fun showRequestError(result:List<GeneralError?>?) {
        _state.value = _state.value.copy(
            errorInfo = errorMessage(result)
        )
    }
    private fun showException(e: Exception) {
        if (!e.toString().contains(ApiUtils.Status.ERR_401)){
            _state.value = _state.value.copy(
                errorInfo = e.message.toString()
            )
        }
    }
}
